import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogOut, LayoutDashboard, Menu, X, ChevronRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";

const nav = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/shop?category=Liquid+Cultures", label: "Cultures" },
  { to: "/shop?category=Grow+Bags+%26+Buckets", label: "Grow Bags" },
  { to: "/shop?category=Ebooks+%26+Guides", label: "Guides" },
  { to: "/blog", label: "Blog" },
  { to: "/free-guide", label: "Free Guide" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobile, setMobile] = useState(false);

  return (
    <header className="sticky top-0 z-50 glass bg-background border-b border-border">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <img src="/nc-logo.png" alt="NC Mycology" className="h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-105 group-hover:rotate-[-2deg]" />
          <span className="font-display text-2xl font-semibold tracking-tight leading-none hidden sm:inline">NC&nbsp;Mycology</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {nav.map((n) => (
            <Link key={n.label} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
              className="nav-underline font-mono-label text-xs text-muted-foreground hover:text-primary transition-colors">
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="nav-account-menu" className="rounded-full">
                  <User className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 text-sm">
                  <p className="font-medium truncate">{user.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/account")} data-testid="nav-my-orders">
                  <ShoppingBag className="w-4 h-4 mr-2" /> My Orders & Downloads
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="nav-admin">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Admin Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="nav-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => navigate("/login")}
              data-testid="nav-login" className="font-mono-label text-xs hidden sm:inline-flex">
              Sign In
            </Button>
          )}

          <button onClick={() => setOpen(true)} data-testid="nav-cart-button"
            className="relative rounded-full p-2 hover:bg-accent transition-colors">
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span data-testid="nav-cart-count"
                className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] font-mono font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </button>

          <button onClick={() => setMobile((m) => !m)} data-testid="nav-mobile-toggle" aria-label="Menu"
            className="md:hidden inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 hover:border-primary transition-colors">
            {mobile ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            <span className="font-mono-label text-[10px]">{mobile ? "Close" : "Menu"}</span>
          </button>
        </div>
      </div>

      {mobile && (
        <div className="md:hidden border-t border-border bg-background" data-testid="mobile-menu">
          <nav className="flex flex-col divide-y divide-border px-5">
            {nav.map((n) => (
              <Link key={n.label} to={n.to} onClick={() => setMobile(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className="flex items-center justify-between py-4 font-display text-lg hover:text-primary transition-colors">
                {n.label}
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </Link>
            ))}
            {!user ? (
              <Link to="/login" onClick={() => setMobile(false)} data-testid="mobile-nav-signin"
                className="flex items-center justify-between py-4 font-display text-lg text-primary">
                Sign In <ChevronRight className="w-4 h-4" />
              </Link>
            ) : (
              <>
                <Link to="/account" onClick={() => setMobile(false)} data-testid="mobile-nav-account"
                  className="flex items-center justify-between py-4 font-display text-lg hover:text-primary transition-colors">
                  My Orders &amp; Downloads <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </Link>
                {user.role === "admin" && (
                  <Link to="/admin" onClick={() => setMobile(false)} data-testid="mobile-nav-admin"
                    className="flex items-center justify-between py-4 font-display text-lg hover:text-primary transition-colors">
                    Admin Dashboard <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </Link>
                )}
                <button onClick={() => { setMobile(false); logout(); }} data-testid="mobile-nav-logout"
                  className="flex items-center justify-between py-4 font-display text-lg text-left hover:text-destructive transition-colors">
                  Log out <LogOut className="w-4 h-4 text-muted-foreground" />
                </button>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
